/**
 * useHostBrokerage — which brokerage, if any, owns the address the web app is
 * served on, so `/` can render that brokerage's page IN PLACE (spec 011).
 *
 * A platform subdomain (`<slug>.<platform domain>`) is recognised from its
 * name alone, synchronously — no round trip. A custom domain cannot be: only
 * identity-svc knows which verified domain belongs to which tenant, so it is
 * looked up (`/identity/tenant/by-domain`) and the result cached for the
 * session. A match is that brokerage, or an agent's own page when the domain
 * is an agent's (V060). Anything else — unknown domain, inactive tenant (both
 * 404), network failure — is "none", which leaves the shared landing page that
 * names no brokerage. A failed lookup therefore errs towards showing no broker
 * information, never wrong information.
 *
 * This only ever answers; it never navigates. Rendering in place is what makes
 * `/` the same however the visitor reached it — a full load, a reload, or a
 * Home link — where the old one-shot rewrite in main.tsx ran on boot only.
 *
 * The Shell resolves it once, only for an anonymous visitor at `/`, and hands
 * the answer down through HostBrokerageContext, so a signed-in client's own
 * home is never replaced and the app bar and the page agree on what is shown.
 */
import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { identityApi } from "../auth/passkeyApi";
import {
  currentHostname,
  isCustomDomainCandidate,
  platformSubdomainSlug,
} from "../../shell/host";

export type HostBrokerage =
  | { kind: "checking" }
  | { kind: "none" }
  | { kind: "brokerage"; slug: string; agentId?: string };

const NONE: HostBrokerage = { kind: "none" };
const CHECKING: HostBrokerage = { kind: "checking" };

/** Resolve the host's brokerage; `enabled` false answers "none" without looking. */
export function useHostBrokerage(enabled: boolean): HostBrokerage {
  const [host] = useState(currentHostname);
  const slug = platformSubdomainSlug(host);
  const candidate = slug == null && isCustomDomainCandidate(host);

  const lookup = useQuery({
    queryKey: ["tenant-by-domain", host],
    queryFn: () => identityApi.getTenantByDomain(host),
    enabled: enabled && candidate,
    // A 404 is an answer, not a failure to retry — nor to refetch when another
    // component starts watching it. PublicFooter does, inside the page this
    // answer chose; refetching on its mount flipped the Shell back to
    // "checking", which unmounted the page and looped (spec 024).
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  });

  if (!enabled) return NONE;
  if (slug) return { kind: "brokerage", slug };
  if (!candidate) return NONE;
  if (lookup.isPending) return CHECKING;
  if (!lookup.data) return NONE;
  const tenant = lookup.data;
  return tenant.agent_id
    ? { kind: "brokerage", slug: tenant.slug, agentId: tenant.agent_id }
    : { kind: "brokerage", slug: tenant.slug };
}

export const HostBrokerageContext = createContext<HostBrokerage>(NONE);

/** What the Shell resolved for this address (see useHostBrokerage). */
export function useHostBrokerageResult(): HostBrokerage {
  return useContext(HostBrokerageContext);
}
